import { useQueryClient } from '@tanstack/react-query';
import { type FormEvent, useState } from 'react';

import { candidateBriefQueryKeys } from '@/modules/candidate-detail/queries/queryKeys';
import type { CandidateDetailData } from '@/modules/candidate-detail/types';
import { useCreateCandidateJobNoteMutation } from '@/modules/candidates/mutations/useCreateCandidateJobNoteMutation';
import type { CandidateStatusApi } from '@/modules/candidates/types';
import { NoteText } from '@/modules/notes/components/NoteText';
import { Button } from '@/modules/ui/components/button/Button';
import { InlineError } from '@/modules/ui/components/inline-error/InlineError';
import { Textarea } from '@/modules/ui/components/textarea/Textarea';
import { actionErrorMessage } from '@/utils/actionErrorMessage';

import { CandidateSectionTitle } from './CandidateSectionTitle';

interface HiringManagerPrescreenNotesProps {
  candidate: CandidateDetailData;
  jobId: string;
  status: CandidateStatusApi | null;
}

const formatNoteDate = (value: string) =>
  new Date(value).toLocaleDateString(undefined, {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  });

/**
 * On jobs the hiring manager screens themselves: the notes from their call,
 * recorded once from Contacted and shown exactly as typed. Saving moves the
 * candidate to Prescreened, which starts the usual assessment and submission.
 */
export function HiringManagerPrescreenNotes({
  candidate,
  jobId,
  status,
}: HiringManagerPrescreenNotesProps) {
  const note = candidate.prescreenNote;

  if (note) {
    const author = [note.authorFirstName, note.authorLastName]
      .filter(Boolean)
      .join(' ');
    return (
      <section
        aria-labelledby="hm-prescreen-notes-heading"
        className="flex flex-col gap-3"
      >
        <CandidateSectionTitle
          hint={[author, formatNoteDate(note.createdAt)]
            .filter(Boolean)
            .join(' · ')}
          id="hm-prescreen-notes-heading"
        >
          Pre-screen notes
        </CandidateSectionTitle>
        <NoteText
          className="max-w-[64ch] text-[15px] leading-6 text-inherit"
          text={note.content}
        />
      </section>
    );
  }

  if (status === 'Contacted') {
    return <PrescreenNotesForm candidate={candidate} jobId={jobId} />;
  }

  if (status === 'ContactStarted') {
    return (
      <p className="text-muted-foreground border-y py-4 text-[13px]">
        Finlay is still waiting for a reply from {candidate.firstName}.
      </p>
    );
  }

  return null;
}

function PrescreenNotesForm({
  candidate,
  jobId,
}: {
  candidate: CandidateDetailData;
  jobId: string;
}) {
  const [content, setContent] = useState('');
  const [error, setError] = useState<string | null>(null);
  const createNote = useCreateCandidateJobNoteMutation();
  const queryClient = useQueryClient();

  const submit = (event: FormEvent) => {
    event.preventDefault();
    if (!content.trim()) {
      setError('Add your notes from the call before saving.');
      return;
    }
    setError(null);
    createNote.mutate(
      {
        candidateJobId: candidate.candidateJobId,
        body: { type: 'Prescreen', content },
      },
      {
        onSuccess: () =>
          void queryClient.invalidateQueries({
            queryKey: candidateBriefQueryKeys.brief({
              jobId,
              candidateId: candidate.candidateId,
            }),
          }),
        onError: (mutationError) =>
          setError(actionErrorMessage('save your notes', mutationError)),
      },
    );
  };

  return (
    <form
      aria-labelledby="hm-prescreen-form-heading"
      className="flex flex-col gap-3"
      noValidate
      onSubmit={submit}
    >
      <CandidateSectionTitle
        hint="Saved exactly as you type them. You can’t edit them afterwards."
        id="hm-prescreen-form-heading"
      >
        Your pre-screen notes
      </CandidateSectionTitle>
      <label className="sr-only" htmlFor="hm-prescreen-notes">
        What did you learn on the call?
      </label>
      <Textarea
        aria-describedby={error ? 'hm-prescreen-notes-error' : undefined}
        aria-invalid={error !== null}
        className="min-h-32 max-w-[64ch]"
        id="hm-prescreen-notes"
        onChange={(event) => setContent(event.target.value)}
        placeholder={`What did you learn on the call with ${candidate.firstName}?`}
        value={content}
      />
      {error && (
        <InlineError id="hm-prescreen-notes-error">{error}</InlineError>
      )}
      <div>
        <Button
          className="h-8 rounded-md"
          loading={createNote.isPending}
          size="sm"
          type="submit"
        >
          Save notes
        </Button>
      </div>
    </form>
  );
}
